import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAcknowledgeIncident, useResolveIncident, useSetIncidentPublic, useIncidentUpdates, usePostIncidentUpdate, useDeleteIncidentUpdate } from '../../hooks/useIncidents';
import { useToast } from '../common/Toast';
import { Button } from '../common/Button';
import { Input, Select, Textarea, checkboxClass } from '../common/Input';
import { Incident, IncidentUpdateStatus } from '../../types';
import { apiError } from '../../utils/apiError';
import { formatDurationSeconds } from '../../utils/series';
import { formatDateTime, formatTime } from '../../utils/datetime';

interface Props {
  incident: Incident;
  /** Hide the service name when the list is already scoped to one service. */
  showService?: boolean;
}

export function IncidentRow({ incident, showService = true }: Props) {
  const { hasPermission } = useAuth();
  const resolve = useResolveIncident();
  const acknowledge = useAcknowledgeIncident();
  const { addToast } = useToast();

  const isDown = incident.type === 'down';
  const canAct = hasPermission('services.edit');
  const canPublish = hasPermission('status_pages.edit');
  const acknowledged = Boolean(incident.acknowledged_at);
  const [showPublic, setShowPublic] = useState(false);

  return (
    <div className="px-4 py-3 space-y-3">
    <div className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${isDown ? 'bg-status-down' : 'bg-status-degraded'}`}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          {showService && (
            <Link
              to={`/services/${incident.service_id}`}
              className="font-medium text-sm hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
            >
              {incident.service_name}
            </Link>
          )}
          <span className={`text-xs font-medium uppercase tracking-wide ${isDown ? 'text-status-down' : 'text-status-degraded'}`}>
            {incident.type}
          </span>

          {/* Acknowledged-but-open is its own state: still counting, but owned. */}
          {incident.ongoing && acknowledged && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
              Acknowledged
            </span>
          )}
          {incident.ongoing && !acknowledged && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 font-medium">
              Ongoing
            </span>
          )}
          {/* Down and degraded inside one episode share a row; say which it is now. */}
          {incident.ongoing && incident.current_status && incident.current_status !== incident.type && (
            <span className="text-xs text-gray-500 dark:text-gray-400">now {incident.current_status}</span>
          )}
          {incident.flapping && (
            <span
              className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 font-medium"
              title="Went up and down repeatedly inside the flap window; notifications are held until it settles"
            >
              Flapping
            </span>
          )}
          {incident.reopened_count > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400" title="Went down again shortly after recovering, so the same incident was reused">
              reopened ×{incident.reopened_count}
            </span>
          )}
          {incident.escalated_at && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 font-medium" title={`Escalated ${formatDateTime(incident.escalated_at)}`}>
              Escalated
            </span>
          )}
          {incident.public === false && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" title="Hidden from status pages">
              hidden
            </span>
          )}
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono tabular-nums">
          {formatDateTime(incident.started_at)}
          {incident.resolved_at && ` → ${formatTime(incident.resolved_at)}`}
        </p>

        {acknowledged && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Acknowledged {formatDateTime(incident.acknowledged_at)}
            {incident.acknowledged_by_name && ` by ${incident.acknowledged_by_name}`}
          </p>
        )}
        {incident.note && (
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 italic">{incident.note}</p>
        )}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {canPublish && (
          <Button size="sm" variant="ghost" onClick={() => setShowPublic(v => !v)} aria-expanded={showPublic}>
            Status page
          </Button>
        )}
        {canAct && incident.ongoing && (
          <div className="flex gap-1">
            {!acknowledged && (
              <Button
                size="sm"
                variant="ghost"
                loading={acknowledge.isPending}
                onClick={() => acknowledge.mutate(
                  { id: incident.id },
                  {
                    onSuccess: () => addToast('success', 'Incident acknowledged'),
                    onError: err => addToast('error', apiError(err, 'Could not acknowledge')),
                  },
                )}
              >
                Ack
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              loading={resolve.isPending}
              onClick={() => resolve.mutate(incident.id, {
                onSuccess: () => addToast('success', 'Incident closed'),
                onError: err => addToast('error', apiError(err, 'Could not close incident')),
              })}
            >
              Close
            </Button>
          </div>
        )}

        <div className="text-right">
          <p className="text-sm font-mono tabular-nums">
            {incident.ongoing ? '—' : formatDurationSeconds(incident.duration_seconds)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {incident.ongoing ? 'open' : 'duration'}
          </p>
        </div>
      </div>
    </div>
    {showPublic && <PublicPanel incident={incident} />}
    </div>
  );
}

const UPDATE_STATUSES: { value: IncidentUpdateStatus; label: string }[] = [
  { value: 'investigating', label: 'Investigating' },
  { value: 'identified', label: 'Identified' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'resolved', label: 'Resolved' },
];

/**
 * What the status pages say about this incident: whether it shows at all,
 * the client-facing title, and the running updates ("Identified: database
 * failover"). Internal notes and error strings never leave the dashboard.
 */
function PublicPanel({ incident }: { incident: Incident }) {
  const { addToast } = useToast();
  const setPublic = useSetIncidentPublic();
  const { data: updates } = useIncidentUpdates(incident.id);
  const post = usePostIncidentUpdate();
  const remove = useDeleteIncidentUpdate();
  const [title, setTitle] = useState(incident.public_title ?? '');
  const [form, setForm] = useState({ status: (incident.ongoing ? 'investigating' : 'resolved') as IncidentUpdateStatus, title: '', body: '' });

  const saveTitle = () => setPublic.mutate({ id: incident.id, data: { public_title: title.trim() || null } }, {
    onSuccess: () => addToast('success', 'Public title saved'),
    onError: err => addToast('error', apiError(err, 'Could not save')),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    post.mutate({ id: incident.id, data: { status: form.status, title: form.title, body: form.body } }, {
      onSuccess: () => { addToast('success', 'Update posted'); setForm(f => ({ ...f, title: '', body: '' })); },
      onError: err => addToast('error', apiError(err, 'Could not post the update')),
    });
  };

  return (
    <div className="ml-5 rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-3 text-sm" data-testid="public-panel">
      <div className="flex items-center gap-4 flex-wrap">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className={checkboxClass}
            checked={incident.public !== false}
            onChange={e => setPublic.mutate({ id: incident.id, data: { public: e.target.checked } }, {
              onError: err => addToast('error', apiError(err, 'Could not change visibility')),
            })}
          />
          Show on status pages
        </label>
        <div className="flex items-center gap-2 flex-1 min-w-[16rem]">
          <Input aria-label="Public title" placeholder={`${incident.service_name} ${incident.type === 'degraded' ? 'degraded performance' : 'outage'}`} value={title} onChange={e => setTitle(e.target.value)} />
          <Button size="sm" variant="secondary" onClick={saveTitle} loading={setPublic.isPending}>Save title</Button>
        </div>
      </div>

      {(updates ?? []).length > 0 && (
        <ol className="space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
          {(updates ?? []).map(u => (
            <li key={u.id} className="flex items-start justify-between gap-2">
              <span>
                {u.status && <span className="font-medium mr-1">{UPDATE_STATUSES.find(x => x.value === u.status)?.label ?? u.status} —</span>}{' '}
                {u.title}
                {u.body && <span className="block text-gray-600 dark:text-gray-300">{u.body}</span>}
                <span className="block text-xs text-gray-500 dark:text-gray-400">{formatDateTime(u.created_at)}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => remove.mutate({ id: incident.id, postId: u.id })} aria-label="Delete update">×</Button>
            </li>
          ))}
        </ol>
      )}

      <form onSubmit={submit} className="space-y-2">
        <div className="flex gap-2 flex-wrap">
          <Select aria-label="Update status" className="!w-auto" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as IncidentUpdateStatus }))}>
            {UPDATE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <Input aria-label="Update title" className="!flex-1 !w-auto" required placeholder="What you want visitors to read" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <Textarea aria-label="Update details" rows={2} placeholder="Optional detail" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
        <Button type="submit" size="sm" loading={post.isPending}>Post update</Button>
      </form>
    </div>
  );
}
